import {
  analyticsChangedFieldSchema,
  reportFilterStateV1Schema,
  type ReportFilterStateV1,
} from "@meta-video/contracts";
import { useCallback, useMemo, useRef } from "react";

import {
  useAnalytics,
  useAnalyticsSessionId,
} from "../../shared/analytics/AnalyticsProvider.js";
import { analyticsEvents } from "../../shared/analytics/events.js";

export interface AcknowledgedFilterChanges {
  readonly changeFilters: (filters: ReportFilterStateV1) => boolean;
  readonly consumeAcknowledgement: () => boolean;
}

function filterStateKey(filters: ReportFilterStateV1): string {
  const parsed = reportFilterStateV1Schema.safeParse(filters);
  return JSON.stringify(parsed.success ? parsed.data : filters);
}

const trackedFilterFields = analyticsChangedFieldSchema.exclude(["all"]).options;

export function changedFilterFields(
  current: ReportFilterStateV1,
  next: ReportFilterStateV1,
): (typeof trackedFilterFields)[number][] {
  return trackedFilterFields.filter(
    (field) => JSON.stringify(current[field]) !== JSON.stringify(next[field]),
  );
}

export function useAcknowledgedFilterChanges({
  filters,
  onChange,
}: {
  readonly filters: ReportFilterStateV1;
  readonly onChange: (filters: ReportFilterStateV1) => boolean | void;
}): AcknowledgedFilterChanges {
  const analytics = useAnalytics();
  const sessionId = useAnalyticsSessionId();
  const pendingAcknowledgementsRef = useRef<string[]>([]);
  const lastCapturedTransitionRef = useRef<string | null>(null);
  const latestFiltersRef = useRef(filters);
  latestFiltersRef.current = filters;
  const filtersKey = useMemo(() => filterStateKey(filters), [filters]);
  const latestFiltersKeyRef = useRef(filtersKey);
  latestFiltersKeyRef.current = filtersKey;

  const changeFilters = useCallback((nextFilters: ReportFilterStateV1): boolean => {
    const nextKey = filterStateKey(nextFilters);
    const expectsAcknowledgement = nextKey !== latestFiltersKeyRef.current;
    if (expectsAcknowledgement) pendingAcknowledgementsRef.current.push(nextKey);
    let accepted = false;
    try {
      accepted = onChange(nextFilters) !== false;
      if (accepted && expectsAcknowledgement && sessionId) {
        const transitionKey = `${latestFiltersKeyRef.current}->${nextKey}`;
        if (lastCapturedTransitionRef.current !== transitionKey) {
          const changedFields = changedFilterFields(
            latestFiltersRef.current,
            nextFilters,
          );
          if (changedFields.length > 0) {
            const nextSearch = nextFilters.adNameSearch;
            const previousSearch = latestFiltersRef.current.adNameSearch;
            analytics.capture(
              nextSearch !== null && nextSearch !== previousSearch
                ? analyticsEvents.searchSubmitted(sessionId, {
                    changedFields,
                    filters: nextFilters,
                    query: nextSearch,
                  })
                : analyticsEvents.filtersApplied(sessionId, {
                    changedFields,
                    filters: nextFilters,
                  }),
            );
            lastCapturedTransitionRef.current = transitionKey;
          }
        }
      }
      return accepted;
    } finally {
      if (!accepted && expectsAcknowledgement) {
        const pendingIndex = pendingAcknowledgementsRef.current.lastIndexOf(nextKey);
        if (pendingIndex >= 0) pendingAcknowledgementsRef.current.splice(pendingIndex, 1);
      }
    }
  }, [analytics, onChange, sessionId]);

  const consumeAcknowledgement = useCallback((): boolean => {
    const acknowledgementIndex = pendingAcknowledgementsRef.current.indexOf(filtersKey);
    if (acknowledgementIndex >= 0) {
      pendingAcknowledgementsRef.current.splice(0, acknowledgementIndex + 1);
      return true;
    }
    pendingAcknowledgementsRef.current = [];
    return false;
  }, [filtersKey]);

  return { changeFilters, consumeAcknowledgement };
}
